import service from '@/api/doctorTherapies';
import {loading, alert, handleError} from '@/state/generators/helpers';

const SUMMARY_STATUSES = ['active', 'paused'];

export const state = {
    loading: false,
    service: service('doctor'),
    pseudonymId: null,
    therapies: [],
    compliance: null,
    complianceSummary: {},
    complianceSummaryLoading: false,
    engagement: null,
    engagementLoading: false,
    patients: [],
    components: [],
    medicines: [],
    catalogLoaded: false,
    item: {},
    errors: {}
};

export const getters = {
    loading: function (state) {
        return !!state.loading;
    },
    pseudonymId: function (state) {
        return state.pseudonymId;
    },
    therapies: function (state) {
        return state.therapies;
    },
    compliance: function (state) {
        return state.compliance ? JSON.parse(JSON.stringify(state.compliance)) : null;
    },
    complianceSummary: function (state) {
        return state.complianceSummary;
    },
    complianceSummaryLoading: function (state) {
        return !!state.complianceSummaryLoading;
    },
    engagement: function (state) {
        return state.engagement ? JSON.parse(JSON.stringify(state.engagement)) : null;
    },
    engagementLoading: function (state) {
        return !!state.engagementLoading;
    },
    patients: function (state) {
        return state.patients;
    },
    components: function (state) {
        return state.components;
    },
    medicines: function (state) {
        return state.medicines;
    },
    catalogLoaded: function (state) {
        return !!state.catalogLoaded;
    },
    item: function (state) {
        return JSON.parse(JSON.stringify(state.item));
    },
    errors: function (state) {
        const errors = Object.assign({}, (state.errors || {}));

        Object.keys(errors).forEach(key => {
            errors[key] = errors[key].join(' ');
        });

        return errors;
    },
    states: function (state) {
        const states = {};

        Object.keys(state.errors || {}).forEach(key => {
            states[key] = false;
        });

        return states;
    }
};

export const mutations = {
    'SET_LOADING': function (state, boolean = true) {
        state.loading = boolean;
    },
    'SET_PSEUDONYM_ID': function (state, pseudonymId = null) {
        state.pseudonymId = pseudonymId;
    },
    'SET_THERAPIES': function (state, therapies = []) {
        state.therapies = therapies;
    },
    'ADD_THERAPY': function (state, therapy) {
        state.therapies.unshift(therapy);
    },
    'REPLACE_THERAPY': function (state, therapy) {
        const index = state.therapies.findIndex(item => item.id === therapy.id);

        if (index !== -1) {
            state.therapies[index] = therapy;
            state.therapies = JSON.parse(JSON.stringify(state.therapies));
        }
    },
    'SET_COMPLIANCE': function (state, compliance = null) {
        state.compliance = compliance;
    },
    'SET_COMPLIANCE_SUMMARY': function (state, summary = {}) {
        state.complianceSummary = summary;
    },
    'SET_COMPLIANCE_SUMMARY_LOADING': function (state, boolean = true) {
        state.complianceSummaryLoading = boolean;
    },
    'SET_ENGAGEMENT': function (state, engagement = null) {
        state.engagement = engagement;
    },
    'SET_ENGAGEMENT_LOADING': function (state, boolean = true) {
        state.engagementLoading = boolean;
    },
    'SET_PATIENTS': function (state, patients = []) {
        state.patients = patients;
    },
    'SET_COMPONENTS': function (state, components = []) {
        state.components = components;
    },
    'SET_MEDICINES': function (state, medicines = []) {
        state.medicines = medicines;
    },
    'SET_CATALOG_LOADED': function (state, boolean = true) {
        state.catalogLoaded = boolean;
    },
    'SET_ITEM': function (state, item = {}) {
        state.item = item;
    },
    'SET_ERRORS': function (state, errors = {}) {
        state.errors = errors;
    }
};

export const actions = {
    therapies: function ({commit, state}, {pseudonymId, params}) {
        loading(commit);
        commit('SET_PSEUDONYM_ID', pseudonymId);

        return state.service.fetchForPatient(pseudonymId, params).then(response => {
            commit('SET_THERAPIES', response.data);
            loading(commit, false);
            return response.data;
        }).catch((e) => {
            return handleError(e, commit);
        });
    },

    storeMedical: function ({commit, state}, payload) {
        loading(commit);

        return state.service.storeMedical(payload).then(response => {
            if (state.pseudonymId && payload.patient_pseudonym_id === state.pseudonymId) {
                commit('ADD_THERAPY', response.data);
            }
            commit('SET_ITEM', response.data);
            commit('SET_ERRORS', {});
            alert(commit, this.$t('request.creation_is_successful'));
            loading(commit, false);
            return response.data;
        }).catch((e) => {
            return handleError(e, commit);
        });
    },

    compliance: function ({commit, state}, id) {
        loading(commit);

        return state.service.fetchComplianceDetails(id).then(response => {
            commit('SET_COMPLIANCE', response.data);
            loading(commit, false);
            return response.data;
        }).catch((e) => {
            return handleError(e, commit);
        });
    },

    complianceSummary: function ({commit, state}, therapies) {
        const list = (therapies || []).filter(therapy => {
            return SUMMARY_STATUSES.includes(therapy.status);
        });

        const known = state.complianceSummary || {};
        const kept = {};

        list.forEach(therapy => {
            if (known[therapy.id]) {
                kept[therapy.id] = known[therapy.id];
            }
        });

        const pending = list.filter(therapy => !kept[therapy.id]);

        if (!pending.length) {
            commit('SET_COMPLIANCE_SUMMARY', kept);
            commit('SET_COMPLIANCE_SUMMARY_LOADING', false);

            return Promise.resolve(kept);
        }

        commit('SET_COMPLIANCE_SUMMARY_LOADING', true);

        const summary = Object.assign({}, kept);

        return pending.reduce((chain, therapy) => {
            return chain.then(() => {
                return state.service.fetchCompliance(therapy.id).then(response => {
                    if (response) {
                        summary[therapy.id] = response;
                    }
                }).catch(() => {
                    return null;
                });
            });
        }, Promise.resolve()).then(() => {
            commit('SET_COMPLIANCE_SUMMARY', summary);
            commit('SET_COMPLIANCE_SUMMARY_LOADING', false);

            return summary;
        });
    },

    engagement: function ({commit, state}, pseudonymId) {
        commit('SET_ENGAGEMENT_LOADING', true);

        return state.service.fetchEngagement(pseudonymId).then(response => {
            commit('SET_ENGAGEMENT', response.data);
            commit('SET_ENGAGEMENT_LOADING', false);
            return response.data;
        }).catch(() => {
            commit('SET_ENGAGEMENT', null);
            commit('SET_ENGAGEMENT_LOADING', false);
            return null;
        });
    },

    loadCatalog: function ({commit, state}, force) {
        if (state.catalogLoaded && !force) {
            return Promise.resolve();
        }

        return Promise.all([
            state.service.fetchPatients(),
            state.service.fetchComponents(),
            state.service.fetchMedicines()
        ]).then(([patients, components, medicines]) => {
            commit('SET_PATIENTS', patients.data || []);
            commit('SET_COMPONENTS', components.data || []);
            commit('SET_MEDICINES', medicines.data || []);
            commit('SET_CATALOG_LOADED', true);
        }).catch((e) => {
            return handleError(e, commit);
        });
    },

    activate: function (context, id) {
        return transition(context, this, 'activate', id);
    },

    pause: function (context, id) {
        return transition(context, this, 'pause', id);
    },

    resume: function (context, id) {
        return transition(context, this, 'resume', id);
    },

    complete: function (context, id) {
        return transition(context, this, 'complete', id);
    },

    abort: function (context, {id, data}) {
        return transition(context, this, 'abort', id, data);
    },

    clearCompliance: function ({commit}) {
        commit('SET_COMPLIANCE', null);
    },

    clearComplianceSummary: function ({commit}) {
        commit('SET_COMPLIANCE_SUMMARY', {});
        commit('SET_COMPLIANCE_SUMMARY_LOADING', false);
    },

    clearEngagement: function ({commit}) {
        commit('SET_ENGAGEMENT', null);
    },

    clearErrors: function ({commit}) {
        commit('SET_ERRORS', {});
    },

    clearModule: function ({commit}) {
        commit('SET_PSEUDONYM_ID', null);
        commit('SET_THERAPIES', []);
        commit('SET_COMPLIANCE', null);
        commit('SET_COMPLIANCE_SUMMARY', {});
        commit('SET_ENGAGEMENT', null);
        commit('SET_ITEM', {});
        commit('SET_ERRORS', {});
    }
};

function transition({commit, state}, store, action, id, data) {
    loading(commit);

    return state.service[action](id, data).then(response => {
        commit('REPLACE_THERAPY', response.data);
        commit('SET_ITEM', response.data);
        alert(commit, store.$t('request.save_is_successful'));
        loading(commit, false);
        return response.data;
    }).catch((e) => {
        return handleError(e, commit);
    });
}
